import { TickDouble02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { Tabs } from "shared/components/BaseTabs";
import type { Tab } from "shared/components/BaseTabs/types";
import { NotificationKind } from "../types";

interface LayoutHeaderProps {
  unreadOnly: boolean;
  toggleUnreadOnly: () => void;
  tab: string;
  onTabChange: (tab: string) => void;
  unreadTotalCount: number;
  unreadMentionCount: number;
  unreadAssignedCount: number;
  unreadMessageCount: number;
  unreadCallCount: number;
  unreadAiAgentCount: number;
  // Voice is a partner feature; hide the tab entirely rather than showing an
  // empty one, the way every other voice affordance in the portal behaves.
  showCallsTab: boolean;
  // AI agent escalations: only with the AI Leasing Agent on.
  showAiAgentsTab: boolean;
  isMarkingAllAsRead: boolean;
  onMarkAllAsRead: () => void;
}

const renderTabLabel = (label: string, count: number) => (
  <span>
    {label}{" "}
    {count ? (
      <span className="px-1.5 py-0.5 bg-primary-500 text-white text-xs rounded-md font-bold">
        {count}
      </span>
    ) : (
      ""
    )}
  </span>
);

export const LayoutHeader = (props: LayoutHeaderProps) => {
  const {
    unreadOnly,
    toggleUnreadOnly,
    tab,
    onTabChange,
    unreadTotalCount,
    unreadMentionCount,
    unreadAssignedCount,
    unreadMessageCount,
    unreadCallCount,
    unreadAiAgentCount,
    showCallsTab,
    showAiAgentsTab,
    isMarkingAllAsRead,
    onMarkAllAsRead
  } = props;

  const tabs: Tab<string>[] = [
    // Leads the row, as designed: a missed call is the most time-sensitive
    // thing the bell can hold.
    ...(showCallsTab
      ? [
          {
            value: NotificationKind.Call,
            label: renderTabLabel("Calls", unreadCallCount)
          }
        ]
      : []),
    // A caller the AI agent escalated is as time-sensitive as a missed call.
    ...(showAiAgentsTab
      ? [
          {
            value: NotificationKind.AiAgent,
            label: renderTabLabel("AI Agents", unreadAiAgentCount)
          }
        ]
      : []),
    {
      value: NotificationKind.Mention,
      label: renderTabLabel("Mentions", unreadMentionCount)
    },
    {
      value: NotificationKind.Assigned,
      label: renderTabLabel("Assigned", unreadAssignedCount)
    },
    {
      value: NotificationKind.Message,
      label: renderTabLabel("Messages", unreadMessageCount)
    }
  ];

  return (
    <div className="divide-y divide-alpha-40">
      <div className="flex items-center gap-8 p-6">
        <div className="flex flex-1 items-center gap-4 min-w-0">
          <h1 className="text-xl font-bold leading-8 text-content-1000">
            Notifications
          </h1>
          <Switch
            label="Only unread"
            size={SwitchSize.SM}
            checked={unreadOnly}
            onChange={() => toggleUnreadOnly()}
          />
        </div>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          className="shadow-xs"
          HugeIcon={TickDouble02Icon}
          disabled={unreadTotalCount === 0 || isMarkingAllAsRead}
          onClick={() => onMarkAllAsRead()}
        >
          Mark all as read
        </Button>
      </div>

      <div className="p-4 pb-0">
        <Tabs
          size="sm"
          tabStyle="line"
          fullWidthTabs={false}
          tabs={tabs}
          active={tab}
          onChange={(t) => onTabChange(t)}
        />
      </div>
    </div>
  );
};
